import qrcode from "qrcode-generator";
import { useMemo } from "react";
import styles from "./QrCode.module.css";

/**
 * QrCode — a scannable QR code generated fully in-process, no network
 * request, no `eval`/`new Function`. Renders as a data:image/svg+xml `<img>`.
 *
 * Mirrors Signal Desktop's shipped approach (verified at source level in
 * Signal-Desktop@main): `qrcode(typeNumber, errorCorrectionLevel)` ->
 * `addData(value)` -> `make()` -> `createSvgTag({...})` -> data URI on an
 * `<img src>`. Library: `qrcode-generator` (MIT, zero deps, ~7.2KB gz),
 * chosen specifically because it emits SVG with no eval/WASM/remote-code
 * paths — required by the extension's `script-src 'self' 'wasm-unsafe-eval'`
 * CSP and its dist-remote-code build gate.
 *
 * Encoding parameters are fixed, not configurable: type number `0` lets the
 * encoder auto-pick the smallest QR version that fits `value` (so a long
 * Play Store URL still encodes without a manual version bump), and error
 * correction `"M"` (~15% recovery) is the common default and tolerates the
 * modest damage a phone camera photographing a screen introduces. `"L"`
 * would pack more data per module, but a QR that fails to scan is the whole
 * feature failing.
 *
 * SECURITY: `value` is rendered ONLY as an encoded QR image — never as
 * visible text, never as an attribute value, never logged. Today it is
 * always a public install URL, but this primitive must stay safe if a
 * future caller ever passes something sensitive.
 */
export interface QrCodeProps {
  /** URL to encode. Never a secret — see the security note above. */
  value: string;
  /** Accessible name. REQUIRED: a QR is an image and needs a text equivalent. */
  label: string;
  /** Rendered px size. Default 120. */
  size?: number;
  className?: string;
}

const DEFAULT_SIZE = 120;

// Auto type number (0): the encoder grows the QR version to fit whatever
// `value` needs. Error correction "M": the common default, tolerant of the
// blur/glare/angle a phone photographing a screen introduces. See the
// design spec (P6) for why "L" was rejected — it saves modules at the cost
// of scan reliability, which is not an acceptable trade for an install QR.
const TYPE_NUMBER = 0;
const ERROR_CORRECTION_LEVEL = "M";

export function QrCode({ value, label, size = DEFAULT_SIZE, className }: QrCodeProps) {
  const src = useMemo(() => {
    const qr = qrcode(TYPE_NUMBER, ERROR_CORRECTION_LEVEL);
    qr.addData(value);
    qr.make();
    // `scalable: true` omits fixed width/height from the generated SVG
    // (viewBox only), so the rendered box is controlled entirely by the
    // <img>'s own width/height attributes below — no per-size cell-size
    // math, no rounding artefacts, and the library's default margin (4
    // modules, the QR spec's quiet zone) is preserved regardless of `size`.
    // Because of this, `size` does not affect the generated markup and is
    // deliberately not a dependency of this memo.
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr.createSvgTag({ scalable: true }))}`;
  }, [value]);

  const classes = [styles.qrCode, className].filter(Boolean).join(" ");
  return <img src={src} alt={label} width={size} height={size} className={classes} />;
}
